import { UseMutateAsyncFunction, useMutation, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router"
import { AUTH_TOKEN_KEY, SELECTED_WORKSPACE_KEY, USER_DETAILS_KEY } from "../../../constants"
import { useAxios } from "../../../contexts/AxiosContext"
import { StorageService } from "../../../services"
import { User, UserProfile, Workspace } from "../../../types"
import { resolveWorkspaceSelection } from "../../../utils/workspaceSelection"
import { TriggerToast } from "../../globals/Toaster"
import { LoginResponse, useAuthService } from "../services/useAuthService"
import { appRoutes } from "../../../routes/appRoutes"
import { syncWebExtensionAuth } from "../../../services/WebExtensionBridge"
import { isExtensionRuntime } from "../../../utils/appSurface"

type loginMutationType = UseMutateAsyncFunction<LoginResponse, unknown, {
  email: string;
  password: string;
}, unknown>

type errorType = {
  code: string;
  message: string;
  name: string;
  response: {
    data: {
      message: string;
      statusCode: number;
    }
  },
  status: number;
}

const useLogin = (): loginMutationType => {

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const authService = useAuthService()
  const axios = useAxios()

  const { mutateAsync: loginMutation } = useMutation({
    mutationFn: (params: { email: string; password: string }) => {
      return authService.login(params.email, params.password)
    },
    onSuccess: async (data: { access_token: string, user: User, workspaces: Array<Workspace>, active_workspace: Array<Workspace> }) => {
      const workspaces = (data.workspaces ?? []).filter(workspace => workspace?.id)
      const activeWorkspaces = (data.active_workspace ?? []).filter(workspace => workspace?.id)

      axios.setToken(data.access_token)
      await StorageService.setStorageItem(AUTH_TOKEN_KEY, data.access_token)

      const workspaceId = resolveWorkspaceSelection({
        workspaces,
        activeWorkspaces,
      })

      if (workspaceId) {
        await StorageService.setStorageItem(SELECTED_WORKSPACE_KEY, workspaceId)
      } else {
        await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY)
      }

      queryClient.removeQueries()
      queryClient.setQueryData<UserProfile>([USER_DETAILS_KEY], {
        user: data.user,
        workspaces,
        active_workspace: activeWorkspaces,
      })

      if (!isExtensionRuntime()) {
        await syncWebExtensionAuth(data.access_token, workspaceId)
      }

      navigate(workspaces.length > 0 ? appRoutes.experiments.root : appRoutes.workspace.noWorkspace, { replace: true })
    },
    onError: (error: errorType) => {
      // show error message to the user toast
      if (error.response.data.message) {
        TriggerToast(error.response.data.message, undefined, 'error');
      } else {
        TriggerToast(error.message, undefined, 'error');
      }
    }
  })

  return loginMutation
}

export default useLogin
